import { getUsageLimits } from './usage-limit-selectors';
import {
    setUsageLimit,
    updateAnimationSecondsUsedInCycle,
    updateTrialAnimationSecondsUsed,
} from './usage-limit-slice';

import { ApiService } from 'js/src/client';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

/**
 * Fetches and sets the usage limits for the current user's team
 * @param excluded_video_id We typically do not want to include the current video in the usage limits because it's still in progress and will be added in the frontend
 */
export function loadUsageLimits(excluded_video_id: number | undefined = undefined) {
    return (dispatch: AppDispatch) => {
        ApiService.usageLimitsRetrieve({
            query: { excluded_video_id: excluded_video_id },
        })
            .then((response) => {
                dispatch(setUsageLimit(response.data));
            })
            .catch((error) => {
                ErrorReporter.capture(error);
            });
    };
}

/**
 * Rolls back an optimistic animation-seconds charge after a generation failure.
 * The authoritative refund happens server-side in AnimationRefundableTask.on_failure;
 * this keeps the UI balance in sync immediately instead of waiting for a page refresh.
 * Routes the rollback to the trial counter for trial users and the paid counter otherwise.
 */
export function applyOptimisticAnimationRefund(secondsToRefund: number) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const usageLimit = getUsageLimits(getState());
        if (!usageLimit) return;

        const secondsLimit = usageLimit.animation_seconds_limit_per_cycle;
        const isPaidPlan = secondsLimit !== null && secondsLimit > 0;

        if (isPaidPlan) {
            const usedSeconds = usageLimit.animation_seconds_used_in_cycle ?? 0;
            dispatch(updateAnimationSecondsUsedInCycle(Math.max(0, usedSeconds - secondsToRefund)));
        } else {
            const trialUsed = usageLimit.trial_animation_seconds_used ?? 0;
            dispatch(updateTrialAnimationSecondsUsed(Math.max(0, trialUsed - secondsToRefund)));
        }
    };
}
